import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop, every } from 'underscore';
import { JobFunctionTargetSetting } from '@bingads-webui-campaign/audience-criterion-setting';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { AudienceTargetInfo } from '@bingads-webui-campaign/audience-target-info';
import { criterionHelper } from '@bingads-webui-campaign/audience-campaign-shared';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getJobFunctionTargetStyles } from './styles/job-function-target-styles';

export const formatJobFunction = ({ jobTargets, i18n }) => {
  if (!jobTargets || !jobTargets.length) {
    return i18n.getString(_TL_('Target Jobs'));
  }

  if (jobTargets.length === 1) {
    return i18n.getString(_TL_('Targeted: 1 job'));
  }

  return i18n.getString(_TL_('Targeted: {{count}} jobs'), { count: (jobTargets.length) });
};

const UnstyledJobFunctionPicker = (props) => {
  const {
    classes,
    updateValidation,
    validationProps,
    showExclusion,
  } = props;
  const { data: { jobTargets, negativeJobTargets } } = validationProps;

  const [jobFunctionAssociationRef, setJobFunctionAssociationRef] = useState();

  const activity = useContext(ActivityContext);

  const currentLevelTargetInfo = useMemo(() => (
    new AudienceTargetInfo({
      resourceName: 'AdGroupCriterion',
      isForVNext: true,
      initAudienceAssociationView: false,
    })), []);

  const jobFunctionAssociationView = useMemo(() => {
    const jobFunctionView = new JobFunctionTargetSetting({
      criterionType: 'JobFunction',
      type: 'JobFunction',
      showBidOnlyNote: false,
      showExclusion,
      defaultBidAdjustment: 0,
      isForVNext: true,
      currentLevelTargetInfo,
    });

    jobFunctionView.render();

    return jobFunctionView;
  }, [currentLevelTargetInfo, showExclusion]);

  const onChange = () => {
    const updatedItems = jobFunctionAssociationView.currentLevelTargetInfo.getSnapshot(false);

    const jobTarget = updatedItems.filter(({ CriterionType }) => (CriterionType === 'JobFunction'))
      .map(({ Criterion }) => {
        const { JobFunctionCriterion } = Criterion;

        return JobFunctionCriterion.Target;
      });

    const negativeJobTarget = updatedItems.filter(({ CriterionType }) => (CriterionType === 'NegativeJobFunction'))
      .map(({ Criterion }) => {
        const { JobFunctionCriterion } = Criterion;

        return JobFunctionCriterion.Target;
      });

    updateValidation([], { jobTargets: jobTarget, negativeJobTargets: negativeJobTarget });

    if (activity) {
      activity.signal(`onIndustryTargetChange - ${JSON.stringify({ jobFunction: jobTarget })}`);
    }
  };

  useEffect(() => {
    if (jobFunctionAssociationRef) {
      jobFunctionAssociationRef.innerHTML = '';
      jobFunctionAssociationRef.append(jobFunctionAssociationView.el);

      jobFunctionAssociationView.searchView.initializeSearchDataTask.then(() => {
        currentLevelTargetInfo.off('AdGroupCriterionJobFunctionChanged', onChange);

        const allJobTargets = jobFunctionAssociationView.searchView.searchResultView.items.map(item => item);

        if (jobTargets && jobTargets.length > 0) {
          const selectedJobTargets = allJobTargets.filter(({ id }) => jobTargets.includes(id));

          const currentTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === 'JobFunction'));

          // check if the current targets are the same as the selected targets
          const isSame = every(currentTargets, ((target) => {
            const { Criterion } = target;

            const { JobFunctionCriterion } = Criterion;

            return jobTargets.includes(JobFunctionCriterion.Target);
          }));

          if (currentTargets.length !== selectedJobTargets.length || !isSame) {
            selectedJobTargets.forEach((data) => {
              const criterion = criterionHelper.buildLinkedInCriterion(
                'JobFunction',
                data,
                true,
                0,
                0
              );


              currentLevelTargetInfo.createAsync(criterion);
            });
          }
        }

        if (negativeJobTargets && negativeJobTargets.length > 0) {
          const selectedNegativeJobTargets = allJobTargets.filter(({ id }) => negativeJobTargets.includes(id));

          const currentNegativeTargets = currentLevelTargetInfo.getSnapshot(false).filter(({ CriterionType }) => (CriterionType === 'NegativeJobFunction'));

          // check if the current targets are the same as the selected targets
          const isSame = every(currentNegativeTargets, ((target) => {
            const { Criterion } = target;

            const { JobFunctionCriterion } = Criterion;

            return negativeJobTargets.includes(JobFunctionCriterion.Target);
          }));

          if (currentNegativeTargets.length !== selectedNegativeJobTargets.length || !isSame) {
            selectedNegativeJobTargets.forEach((data) => {
              const criterion = criterionHelper.buildLinkedInCriterion(
                'JobFunction',
                data,
                false,
                0,
                0
              );

              currentLevelTargetInfo.createAsync(criterion);
            });
          }
        }
      }).then(() => currentLevelTargetInfo.on('AdGroupCriterionJobFunctionChanged', onChange));

      return () => {
        currentLevelTargetInfo.off('AdGroupCriterionJobFunctionChanged', onChange);
      };
    }

    return noop;
  }, [jobFunctionAssociationRef, jobTargets, negativeJobTargets]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classNames('job-function-picker', classes.pickerContainer)} >
      <div className={classNames('job-function-association-view', 'criterion-setting-JobFunction')} ref={newRef => setJobFunctionAssociationRef(newRef)} />
    </div>
  );
};

UnstyledJobFunctionPicker.propTypes = {
  classes: PropTypes.shape({
    pickerContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      jobTargets: PropTypes.arrayOf(PropTypes.shape([])),
      negativeJobTargets: PropTypes.arrayOf(PropTypes.shape([])),
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  showExclusion: PropTypes.bool,
};

UnstyledJobFunctionPicker.defaultProps = {
  showExclusion: true,
};

export const ValidatedJobFunctionPicker = withDefaultStyles(withCustomValidation(UnstyledJobFunctionPicker), getJobFunctionTargetStyles);

